import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import PersonIcon from '@material-ui/icons/Person';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { ReactComponent as LanguageIcon } from '../../base/images/language_icon.svg';
import { ReactComponent as AccessibilityIcon } from '../../base/images/accessibility-icon.svg';
import React, { useState } from 'react';
import { Box, Divider, Drawer, Grid, InputLabel, Link, Button, Icon } from '@material-ui/core';
import { LanguageDropdown } from '../../common/components/LanguageDropdown';
import { MobileHeaderStyles } from '../styles/mobileHeader';
import { useTranslation } from 'react-i18next';
import { TemplateSettings } from '../../store/interfaces';
import { HeaderResponse } from '../interfaces/headerInterface';
import { IELink } from '../../common/components/IELink';
import { Logo } from './Logo';
import { routeMappings } from '../../constants';
import { useLocation } from 'react-router-dom';
import { getCookie } from '../../common/utils/cookies';
import AccessibilityDropdownList from './AccessibilityDropDownList';

/** Mobile Header Props */
interface MobileHeaderProps {
  userName?: string;
  companyLogo: string;
  basicFlow?: boolean;
  settings: TemplateSettings;
  userSessionActive: boolean;
  header: HeaderResponse;
  isMenuOpen: boolean;
  deadlineDatePassed: boolean;
  isPreviewProject: boolean;
  renderLanguageSelector: boolean;
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  toggleMenuDrawer(open: boolean): (event: React.KeyboardEvent | React.MouseEvent) => void;
  onLanguageChange(): void;
  onUserProfileClick(event: React.KeyboardEvent | React.MouseEvent): void;
  onSignInClick(event: React.KeyboardEvent | React.MouseEvent): void;
  onLogoutClick(event: React.KeyboardEvent | React.MouseEvent): void;
}

/** Mobile Header Props */
const StatelessMobileHeader = (props: MobileHeaderProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    settings,
    basicFlow,
    userSessionActive,
    header,
    userName,
    companyLogo,
    isMenuOpen,
    deadlineDatePassed,
    isPreviewProject,
    renderLanguageSelector,
    setIsMenuOpen,
    toggleMenuDrawer,
    onSignInClick,
    onUserProfileClick,
    onLogoutClick,
    onLanguageChange,
  } = props;

  const location = useLocation();
  const [showLanguageDropDown, setShowLanguageDropDown] = useState(false);
  const [showAccessibilityDropdown, setShowAccessibilityDropdown] = useState(false);

  const { links } = settings;
  const classes = MobileHeaderStyles(settings);

  const tcPlusFlow = getCookie('TcPlusFlag') === '1';

  const closeDrawer = (status: boolean): void => {
    setIsMenuOpen(!status);
  };
  return (
    <>
      {(settings.utilityBar.logo || basicFlow) && (
        <Grid container justify="flex-start">
          <Logo
            className={classes.logo}
            src={companyLogo}
            alt={settings.utilityBar.logo ? settings.utilityBar.logo.name : 'SHL Logo'}
          />
        </Grid>
      )}
      <Grid container justify="flex-end">
        {!isMenuOpen ? (
          <MenuIcon
            className={classes.icon}
            role="button"
            tabIndex={0}
            aria-label={t('clickSeeMenu')}
            onClick={toggleMenuDrawer(true)}
            onKeyDown={toggleMenuDrawer(true)}
          />
        ) : (
          <CloseIcon
            className={classes.icon}
            role="button"
            tabIndex={0}
            aria-label={t('clickHideMenu')}
            onClick={toggleMenuDrawer(false)}
            onKeyDown={toggleMenuDrawer(false)}
          />
        )}
      </Grid>

      <Drawer
        disablePortal
        elevation={0}
        className={classes.drawer}
        classes={{ paper: classes.mobileDrawer }}
        anchor="right"
        open={isMenuOpen}
        BackdropProps={{
          style: {
            background: '#000000',
            opacity: '0.1',
          },
        }}
        onClose={toggleMenuDrawer(false)}>
        <Grid item xs={12}>
          <Grid container className={`${classes.marginTop20}`} justify="flex-start" spacing={2}>
            <Grid item xs={12}>
              {userSessionActive ? (
                <Grid
                  container
                  item
                  xs={12}
                  justify="flex-start"
                  alignItems="center"
                  className={`${classes.userProfile} ${classes.menuItemParent}`}>
                  <PersonIcon aria-label={userName} className={classes.icon} />
                  <span>{userName}</span>
                </Grid>
              ) : // disable sign in button on link invalid page
              deadlineDatePassed || location.pathname.includes(routeMappings['LINK_INVALID']) ? (
                <Button disabled>{t('signIn')}</Button>
              ) : !settings.mfsEnabled ? (
                <Grid
                  container
                  item
                  xs={12}
                  justify="flex-start"
                  className={`${classes.userProfile} ${classes.menuItemParent}`}
                  onClick={onSignInClick}
                  onKeyDown={onSignInClick}>
                  <PersonIcon aria-label={t('signIn')} className={classes.icon} />
                  <span>{t('signIn')}</span>
                </Grid>
              ) : null}
            </Grid>

            {links &&
              links.map(
                (link, index) =>
                  link.label && (
                    <Grid container key={index} item xs={12} className={classes.utilityLink} justify="flex-start">
                      <Link
                        href={link.url ? link.url : '#'}
                        className={classes.menuItem}
                        target={'_blank'}
                        variant="body2"
                        key={index}>
                        <IELink>{link.label.length > 25 ? `${link.label.substring(0, 25)}...` : link.label}</IELink>
                      </Link>
                    </Grid>
                  ),
              )}

            {userSessionActive && (
              <>
                {header && header.showDetails && !settings.mfsEnabled && (
                  <Grid item xs={12}>
                    <Grid container className={classes.menuItem} item xs={12} justify="flex-start">
                      <Link
                        underline="none"
                        className={classes.userProfile}
                        onClick={onUserProfileClick}
                        onKeyDown={onUserProfileClick}
                        tabIndex={0}>
                        <IELink>{t('userProfile')}</IELink>
                      </Link>
                    </Grid>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Grid container className={classes.menuItem} item xs={12} justify="flex-start">
                    {isPreviewProject ? (
                      <Button disabled>{t('signout')}</Button>
                    ) : (
                      <Link
                        underline="none"
                        className={classes.userProfile}
                        onClick={onLogoutClick}
                        onKeyDown={onLogoutClick}
                        tabIndex={0}>
                        <IELink>{t('signout')}</IELink>
                      </Link>
                    )}
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>

          {renderLanguageSelector && (
            <>
              <Grid item xs={12}>
                <Box mt={3} mb={3}>
                  <Divider variant="middle" light className={classes.mobileDivider} />
                </Box>
              </Grid>
              {tcPlusFlow && basicFlow && (
                <Grid item xs={12} className={classes.menuItemParent}>
                  <Grid
                    container
                    className={classes.marginBottom20}
                    onClick={(): void => setShowAccessibilityDropdown(!showAccessibilityDropdown)}
                    item
                    xs={12}
                    justify="flex-start">
                    <Icon>
                      <AccessibilityIcon aria-label={t('accessibility')} className={classes.icon} />
                    </Icon>
                    <InputLabel htmlFor="accessibility" className="accessibility-label-mobile">
                      <b>{t('accessibility')}</b>
                    </InputLabel>
                    {!showAccessibilityDropdown ? (
                      <KeyboardArrowDownIcon className={`${classes.arrowDropDown} ${classes.icon}`} />
                    ) : (
                      <KeyboardArrowUpIcon className={`${classes.arrowDropDown} ${classes.icon}`} />
                    )}
                  </Grid>
                  {showAccessibilityDropdown && (
                    <Grid
                      className={`${classes.marginBottom25} ${classes.subMenuDropDown}`}
                      container
                      item
                      xs={12}
                      justify="center">
                      <AccessibilityDropdownList
                        setOpenAccessibilityMenu={(): boolean => true}
                        closeDrawer={closeDrawer}
                      />
                    </Grid>
                  )}
                </Grid>
              )}

              <Grid item xs={12} className={classes.menuItemParent}>
                <Grid
                  container
                  className={classes.marginBottom20}
                  onClick={(): void => setShowLanguageDropDown(!showLanguageDropDown)}
                  item
                  xs={12}
                  justify="flex-start">
                  <Icon>
                    <LanguageIcon aria-label={t('language')} className={classes.icon} />
                  </Icon>
                  <InputLabel htmlFor="language" className="language-label-mobile">
                    <b>{t('language')}</b>
                  </InputLabel>
                  {!showLanguageDropDown ? (
                    <KeyboardArrowDownIcon className={`${classes.arrowDropDown} ${classes.icon}`} />
                  ) : (
                    <KeyboardArrowUpIcon className={`${classes.arrowDropDown} ${classes.icon}`} />
                  )}
                </Grid>
                {showLanguageDropDown && (
                  <Grid container item xs={12} justify="center" className={classes.subMenuDropDown}>
                    <LanguageDropdown languages={header && header.langGroups} onLanguageChange={onLanguageChange} />
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Drawer>
    </>
  );
};

export const MobileHeader = React.memo(StatelessMobileHeader);
